import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, FolderOpen, UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, EmptyState, LoadingState } from "@tabipla/ui";
import { WarittoLayout } from "../layouts/WarittoLayout";
import { ErrorState } from "../components/ErrorState";
import { TotalBalanceSummary } from "../components/TotalBalanceSummary";
import { GroupBalanceCard } from "../components/GroupBalanceCard";
import { QuickExpenseDialog } from "../components/QuickExpenseDialog";
import { SettlementReminder } from "../components/SettlementReminder";
import { RecentExpensesPreview } from "../components/RecentExpensesPreview";
import { useGroups } from "../../application/hooks/useGroups";
import { useTotalBalance } from "../../application/hooks/useTotalBalance";
import { useRecentExpenses } from "../../application/hooks/useRecentExpenses";
import { useAuth } from "../../application/hooks/useAuth";

// Helper to get random item from array
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: groups, isLoading: groupsLoading, error: groupsError, refetch } = useGroups();
  const { data: balanceSummary, isLoading: balanceLoading } = useTotalBalance();
  const { data: recentExpenses } = useRecentExpenses(10);
  const [showQuickExpense, setShowQuickExpense] = useState(false);

  // Memoize random greeting to keep it stable during the session
  const { greeting, welcomeMessage } = useMemo(() => {
    const hour = new Date().getHours();

    // Get time-based greeting variations
    let greetingVariations: string[];
    if (hour < 12) {
      greetingVariations = t("home.greetings.morningVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.morning")],
      }) as string[];
    } else if (hour < 18) {
      greetingVariations = t("home.greetings.afternoonVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.afternoon")],
      }) as string[];
    } else {
      greetingVariations = t("home.greetings.eveningVariations", {
        returnObjects: true,
        defaultValue: [t("home.greetings.evening")],
      }) as string[];
    }

    // Get welcome message variations
    const welcomeVariations = t("home.welcomeVariations", {
      returnObjects: true,
      defaultValue: [t("home.welcomeBack", { name: "{{name}}" })],
    }) as string[];

    return {
      greeting: getRandomItem(greetingVariations),
      welcomeMessage: getRandomItem(welcomeVariations),
    };
  }, [t]);

  const isLoading = groupsLoading || balanceLoading;
  const hasGroups = groups && groups.length > 0;

  if (isLoading) {
    return (
      <WarittoLayout title={t("nav.home")}>
        <LoadingState message={t("common.loading")} />
      </WarittoLayout>
    );
  }

  if (groupsError) {
    return (
      <WarittoLayout title={t("nav.home")}>
        <ErrorState error={groupsError} onRetry={refetch} />
      </WarittoLayout>
    );
  }

  const hasBalances = balanceSummary && balanceSummary.groups.length > 0;

  // FAB actions
  const fabActions = hasGroups
    ? [
        {
          icon: Plus,
          label: t("expenses.addExpense", "出費を追加"),
          onClick: () => setShowQuickExpense(true),
        },
        {
          icon: UserPlus,
          label: t("groups.createNew", "新しいわりかん"),
          onClick: () => navigate("/groups"),
        },
      ]
    : [
        {
          icon: UserPlus,
          label: t("groups.createNew", "新しいわりかん"),
          onClick: () => navigate("/groups"),
        },
      ];

  return (
    <WarittoLayout title={t("nav.home")} fabActions={fabActions}>
      <div className="animate-fade-in space-y-6">
        {/* Welcome Header */}
        <div>
          <p className="text-muted-foreground">{greeting}</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {user?.name
              ? welcomeMessage.replace("{{name}}", user.name)
              : t("home.welcome", "おかえりなさい！")}
          </h1>
        </div>

        {/* Total Balance Summary */}
        <TotalBalanceSummary
          totalReceivable={balanceSummary?.totalReceivable ?? 0}
          totalPayable={balanceSummary?.totalPayable ?? 0}
        />

        {/* Settlement Reminder */}
        {balanceSummary && (
          <SettlementReminder
            totalReceivable={balanceSummary.totalReceivable}
            totalPayable={balanceSummary.totalPayable}
            groups={balanceSummary.groups}
          />
        )}

        {/* Recent Expenses */}
        {recentExpenses && recentExpenses.length > 0 && groups && (
          <RecentExpensesPreview expenses={recentExpenses} groups={groups} limit={3} />
        )}

        {/* Group Balances */}
        {hasBalances ? (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t("home.groupBalances", "わりかん一覧")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {balanceSummary.groups.map((group) => {
                // Find the full group info to get member count
                const fullGroup = groups?.find((g) => g.id === group.groupId);
                return (
                  <GroupBalanceCard
                    key={group.groupId}
                    groupId={group.groupId}
                    groupName={group.groupName}
                    balance={group.balance}
                    memberCount={fullGroup?.memberCount}
                  />
                );
              })}
            </CardContent>
          </Card>
        ) : hasGroups ? (
          <Card>
            <CardContent className="py-8">
              <div className="text-center">
                <p className="text-muted-foreground">
                  {t("home.noBalances", "精算待ちの残高はありません")}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("home.addExpenseHint", "出費を追加して精算を始めましょう")}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-4">
              <EmptyState
                icon={FolderOpen}
                title={t("groups.noGroups", "まだわりかんがありません")}
                description={t("groups.noGroupsDescription", "わりかんを作成して始めましょう")}
                action={{
                  label: t("groups.createNew", "わりかんを作成"),
                  onClick: () => navigate("/groups"),
                  icon: Plus,
                }}
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Expense Dialog */}
      {hasGroups && (
        <QuickExpenseDialog
          open={showQuickExpense}
          onOpenChange={setShowQuickExpense}
          groups={groups}
        />
      )}
    </WarittoLayout>
  );
}
